import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  Card,
  Button,
  Space,
  Select,
  Input,
  InputNumber,
  Progress,
  Tag,
  Table,
  Typography,
  Row,
  Col,
  message,
  Alert,
  Tooltip,
  QRCode,
  Statistic,
} from 'antd';
import {
  GiftOutlined,
  ArrowLeftOutlined,
  SearchOutlined,
  EyeOutlined,
  SendOutlined,
  CopyOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  SyncOutlined,
  LoadingOutlined,
  ExclamationCircleOutlined,
  SwapOutlined,
} from '@ant-design/icons';
import { apiGet, apiPost } from '../services/api';

const { Title, Text } = Typography;

// 商务风格色彩
const COLORS = {
  primary: '#1677ff',
  secondary: '#722ed1',
  success: '#52c41a',
  warning: '#faad14',
  error: '#ff4d4f',
  // 兼容旧代码的别名
  cyan: '#1677ff',
  purple: '#722ed1',
  pink: '#ff4d4f',
  green: '#52c41a',
  orange: '#faad14',
  yellow: '#faad14',
  darkBg: '#f5f5f5',
  cardBg: '#ffffff',
  borderDark: '#f0f0f0',
};

interface GiftInfo {
  giftId: number;
  giftName: string;
  giftIcon: string;
  giftPrice: number;
}

interface BotResult {
  botId: number;
  walletAddress: string;
  balance: number;
  success: boolean;
  error?: string;
  orderNo?: string;
  transactionHash?: string;
}

interface BotPreview {
  botId: number;
  walletAddress: string;
  userName: string;
  balance: number;
  required: number;
  sufficient: boolean;
  needTransfer?: number;
}

interface PreviewResult {
  needRecharge: boolean;
  needTransfer?: boolean;
  totalBalance: number;
  requiredAmount: number;
  requiredPerBot?: number;
  selectedBots?: BotPreview[];
  selectedCount?: number;
  requestedCount?: number;
  shortfall?: number;
  rechargeAddress?: string;
  rechargeCoin?: string;
  message?: string;
}

interface BatchResult {
  success: boolean;
  needRecharge?: boolean;
  total?: number;
  successCount?: number;
  failCount?: number;
  results?: BotResult[];
  totalBalance?: number;
  requiredAmount?: number;
  shortfall?: number;
  rechargeAddress?: string;
  rechargeCoin?: string;
  message?: string;
  error?: string;
}

interface TaskStep {
  seq: number;
  stepType: string;
  botId: number;
  botName: string;
  walletAddress: string;
  amount: number;
  balanceBefore: number;
  balanceAfter: number;
  fromAddress: string;
  toAddress: string;
  txHash: string;
  status: string;
  errorMsg: string;
  startTime: string;
  endTime: string;
}

interface TaskStatus {
  taskId: string;
  status: string;
  roomId: number;
  giftId: number;
  giftName: string;
  giftPrice: number;
  giftQuantity: number;
  deductCoin: string;
  chainId: number;
  botCount: number;
  totalCost: number;
  successCount: number;
  failedCount: number;
  rechargeAddress: string;
  rechargeAmount: number;
  errorMsg: string;
  createTime: string;
  startTime: string;
  endTime: string;
  steps: TaskStep[];
  totalSteps: number;
  completedSteps: number;
  progress: number;
}

// 简洁页面标题组件
const PageHeader: React.FC<{ icon: React.ReactNode; title: string; subtitle?: string; color?: string }> = ({
  icon, title, subtitle, color = COLORS.primary
}) => (
  <div style={{ marginBottom: 24 }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 4 }}>
      <Link to="/">
        <Button type="text" icon={<ArrowLeftOutlined />} style={{ color: COLORS.primary, fontSize: 14, height: 36, padding: '0 8px' }}>
          返回
        </Button>
      </Link>
      <div style={{
        width: 48, height: 48,
        background: `${color}10`,
        borderRadius: 8,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 24, color: color,
      }}>{icon}</div>
      <div>
        <Title level={2} style={{ margin: 0, fontSize: 24, fontWeight: 600 }}>{title}</Title>
        {subtitle && <Text style={{ color: 'rgba(0, 0, 0, 0.45)', fontSize: 14 }}>{subtitle}</Text>}
      </div>
    </div>
    <div style={{
      width: 120, height: 3, marginLeft: 108,
      background: `linear-gradient(90deg, ${color}, ${color}66, transparent)`,
      borderRadius: 2,
    }} />
  </div>
);

// 简洁卡片组件
const SimpleCard: React.FC<{
  children: React.ReactNode;
  title?: string;
  icon?: React.ReactNode;
  color?: string;
  style?: React.CSSProperties;
}> = ({ children, title, icon, color = COLORS.primary, style }) => (
  <Card
    style={{
      background: COLORS.cardBg,
      border: `1px solid ${COLORS.borderDark}`,
      borderRadius: 12,
      ...style,
    }}
    styles={{
      header: { borderBottom: `1px solid ${COLORS.borderDark}`, background: `${color}08` },
      body: { padding: 24 },
    }}
    title={title && (
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        {icon && <span style={{ color, fontSize: 18 }}>{icon}</span>}
        <span style={{ fontWeight: 600, fontSize: 16 }}>{title}</span>
      </div>
    )}
  >
    {children}
  </Card>
);

// 链选项（与平台支持一致：Solana、BSC、Base、X Layer、Arbitrum）
const CHAIN_OPTIONS = [
  { value: 501, label: 'Solana (501)' },
  { value: 56, label: 'BSC (56)' },
  { value: 8453, label: 'Base (8453)' },
  { value: 196, label: 'X Layer (196)' },
  { value: 42161, label: 'Arbitrum (42161)' },
];

// 获取链名称
const getChainName = (chainId: number): string => {
  const chainNames: { [key: number]: string } = {
    501: 'Solana',
    56: 'BSC',
    8453: 'Base',
    196: 'X Layer',
    42161: 'Arbitrum',
    // 兼容历史任务展示
    137: 'Polygon',
    1: 'Ethereum',
  };
  return chainNames[chainId] || `Chain ${chainId}`;
};

const GiftSend: React.FC = () => {
  // 表单状态
  const [roomId, setRoomId] = useState<string>('');
  const [giftList, setGiftList] = useState<GiftInfo[]>([]);
  const [selectedGiftId, setSelectedGiftId] = useState<number | null>(null);
  const [giftQuantity, setGiftQuantity] = useState<number>(1);
  const [botCount, setBotCount] = useState<number>(1);
  const [deductCoin] = useState<string>('USDT');
  const [chainId, setChainId] = useState<number>(42161);

  // UI 状态
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingGifts, setLoadingGifts] = useState<boolean>(false);
  const [loadingPreview, setLoadingPreview] = useState<boolean>(false);
  const [previewResult, setPreviewResult] = useState<PreviewResult | null>(null);
  const [result, setResult] = useState<BatchResult | null>(null);
  const [error, setError] = useState<string>('');

  // 异步任务状态
  const [taskStatus, setTaskStatus] = useState<TaskStatus | null>(null);
  const [isTaskPolling, setIsTaskPolling] = useState<boolean>(false);
  const taskPollingRef = useRef<NodeJS.Timeout | null>(null);

  // 轮询状态
  const [isPolling, setIsPolling] = useState<boolean>(false);
  const [pollingCountdown, setPollingCountdown] = useState<number>(0);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const countdownIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const previewResultRef = useRef<PreviewResult | null>(null);
  const handlePreviewBotsRef = useRef<((isPollingRequest: boolean) => Promise<void>) | null>(null);

  // 清理轮询
  const stopPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
    setIsPolling(false);
    setPollingCountdown(0);
  }, []);

  // 停止任务轮询
  const stopTaskPolling = useCallback(() => {
    if (taskPollingRef.current) {
      clearInterval(taskPollingRef.current);
      taskPollingRef.current = null;
    }
    setIsTaskPolling(false);
  }, []);

  // 组件卸载时清理轮询
  useEffect(() => {
    return () => {
      stopPolling();
      stopTaskPolling();
    };
  }, [stopPolling, stopTaskPolling]);

  // 获取礼物列表
  const fetchGiftList = async () => {
    if (!roomId) {
      message.error('请输入直播间ID');
      return;
    }

    setLoadingGifts(true);
    setError('');
    setResult(null);
    stopPolling();

    try {
      const data = await apiGet<any>(`/api/gift/list`);

      if (data.code === 0 && data.data && data.data.list) {
        setGiftList(data.data.list);
        if (data.data.list.length > 0) {
          setSelectedGiftId(data.data.list[0].giftId);
        }
        message.success(`成功获取 ${data.data.list.length} 个礼物`);
      } else {
        setError(data.msg || '获取礼物列表失败');
      }
    } catch (err) {
      setError('获取礼物列表失败: ' + (err as Error).message);
    } finally {
      setLoadingGifts(false);
    }
  };

  // 查询充值地址余额
  const checkRechargeBalance = useCallback(async () => {
    const currentPreview = previewResultRef.current;
    if (!currentPreview?.rechargeAddress || !currentPreview?.shortfall) {
      return;
    }

    try {
      const params = new URLSearchParams({
        chainId: chainId.toString(),
        walletAddress: currentPreview.rechargeAddress,
        tokenSymbol: deductCoin,
      });

      const data = await apiGet<any>(`/api/wallet/balance?${params.toString()}`);

      if (data.code === 0 && data.data) {
        const newBalance = parseFloat(data.data.balance || '0');
        const requiredAmount = currentPreview.requiredAmount;

        if (newBalance >= requiredAmount) {
          stopPolling();
          if (handlePreviewBotsRef.current) {
            handlePreviewBotsRef.current(false);
          }
        }
      }
    } catch (err) {
      console.error('查询充值地址余额失败:', err);
    }
  }, [chainId, deductCoin, stopPolling]);

  // 启动轮询
  const startPolling = useCallback(() => {
    stopPolling();
    setIsPolling(true);
    setPollingCountdown(5);

    countdownIntervalRef.current = setInterval(() => {
      setPollingCountdown(prev => (prev <= 1 ? 5 : prev - 1));
    }, 1000);

    pollingIntervalRef.current = setInterval(() => {
      checkRechargeBalance();
    }, 5000);
  }, [checkRechargeBalance, stopPolling]);

  // 预览机器人选择
  const handlePreviewBots = useCallback(async (isPollingRequest: boolean = false) => {
    if (!roomId || !selectedGiftId || giftQuantity <= 0 || botCount <= 0) {
      if (!isPollingRequest) {
        message.error('请填写完整的信息');
      }
      return;
    }

    const selectedGift = giftList.find(g => g.giftId === selectedGiftId);
    if (!selectedGift) {
      if (!isPollingRequest) {
        message.error('请选择礼物');
      }
      return;
    }

    if (!isPollingRequest) {
      setLoadingPreview(true);
      setError('');
      setResult(null);
      setPreviewResult(null);
      previewResultRef.current = null;
      stopPolling();
    }

    try {
      const params = new URLSearchParams({
        roomId: roomId,
        giftPrice: selectedGift.giftPrice.toString(),
        giftQuantity: giftQuantity.toString(),
        deductCoin: deductCoin,
        botCount: botCount.toString(),
        chainId: chainId.toString(),
      });

      const data = await apiGet<any>(`/api/gift/preview-bots?${params.toString()}`);

      if (data.code === 0) {
        setPreviewResult(data.data);
        previewResultRef.current = data.data;

        if (data.data.needRecharge && !isPollingRequest) {
          startPolling();
        }
        if (!data.data.needRecharge && isPollingRequest) {
          stopPolling();
        }
      } else {
        if (!isPollingRequest) {
          setError(data.msg || '预览失败');
        }
      }
    } catch (err) {
      if (!isPollingRequest) {
        setError('预览失败: ' + (err as Error).message);
      }
    } finally {
      if (!isPollingRequest) {
        setLoadingPreview(false);
      }
    }
  }, [roomId, selectedGiftId, giftQuantity, botCount, giftList, deductCoin, chainId, stopPolling, startPolling]);

  useEffect(() => {
    handlePreviewBotsRef.current = handlePreviewBots;
  }, [handlePreviewBots]);

  // 查询任务状态
  const fetchTaskStatus = useCallback(async (taskId: string) => {
    try {
      const data = await apiGet<any>(`/api/gift/task-status?taskId=${taskId}`);

      if (data.code === 0 && data.data) {
        setTaskStatus(data.data);

        const status = data.data.status;
        if (status === 'completed' || status === 'failed' || status === 'cancelled') {
          stopTaskPolling();
          setLoading(false);
        }
      }
    } catch (err) {
      console.error('查询任务状态失败:', err);
    }
  }, [stopTaskPolling]);

  // 启动任务状态轮询
  const startTaskPolling = useCallback((taskId: string) => {
    stopTaskPolling();
    setIsTaskPolling(true);
    fetchTaskStatus(taskId);

    taskPollingRef.current = setInterval(() => {
      fetchTaskStatus(taskId);
    }, 3000);
  }, [fetchTaskStatus, stopTaskPolling]);

  // 发送礼物（异步任务）
  const handleSendGift = async () => {
    if (!roomId || !selectedGiftId || giftQuantity <= 0 || botCount <= 0) {
      message.error('请填写完整的信息');
      return;
    }

    const selectedGift = giftList.find(g => g.giftId === selectedGiftId);
    if (!selectedGift) {
      message.error('请选择礼物');
      return;
    }

    if (previewResult?.needRecharge) {
      message.error('余额不足，请先充值');
      return;
    }

    if (!previewResult?.selectedBots || previewResult.selectedBots.length === 0) {
      message.error('请先预览机器人');
      return;
    }

    const botIds = previewResult.selectedBots.map(bot => bot.botId);

    setLoading(true);
    setError('');
    setResult(null);
    setTaskStatus(null);
    stopPolling();

    try {
      const params = new URLSearchParams({
        roomId: roomId,
        giftId: selectedGiftId.toString(),
        giftPrice: selectedGift.giftPrice.toString(),
        giftQuantity: giftQuantity.toString(),
        deductCoin: deductCoin,
        chainId: chainId.toString(),
      });

      botIds.forEach(id => params.append('botIds', id.toString()));

      const data = await apiPost<any>(`/api/gift/batch-send-async?${params.toString()}`, {});

      if (data.code === 0 && data.data?.taskId) {
        const taskId = data.data.taskId;
        setPreviewResult(null);
        previewResultRef.current = null;

        const currentSelectedGift = giftList.find(g => g.giftId === selectedGiftId);
        const currentBotIds = previewResult?.selectedBots?.map(bot => bot.botId) || [];
        setTaskStatus({
          taskId: taskId,
          status: 'pending',
          roomId: parseInt(roomId),
          giftId: selectedGiftId!,
          giftName: currentSelectedGift?.giftName || '',
          giftPrice: currentSelectedGift?.giftPrice || 0,
          giftQuantity: giftQuantity,
          deductCoin: deductCoin,
          chainId: chainId,
          botCount: currentBotIds.length,
          totalCost: (currentSelectedGift?.giftPrice || 0) * giftQuantity * currentBotIds.length,
          successCount: 0,
          failedCount: 0,
          rechargeAddress: '',
          rechargeAmount: 0,
          errorMsg: '',
          createTime: new Date().toISOString(),
          startTime: '',
          endTime: '',
          steps: previewResult?.selectedBots?.map((bot, index) => ({
            seq: index,
            stepType: 'send_gift',
            botId: bot.botId,
            botName: bot.userName || `Bot-${bot.botId}`,
            walletAddress: bot.walletAddress,
            amount: bot.required,
            balanceBefore: bot.balance,
            balanceAfter: 0,
            fromAddress: '',
            toAddress: '',
            txHash: '',
            status: 'pending',
            errorMsg: '',
            startTime: '',
            endTime: '',
          })) || [],
          totalSteps: currentBotIds.length,
          completedSteps: 0,
          progress: 0,
        });

        startTaskPolling(taskId);
      } else {
        setError(data.msg || '创建打赏任务失败');
        setLoading(false);
      }
    } catch (err) {
      setError('创建打赏任务失败: ' + (err as Error).message);
      setLoading(false);
    }
  };

  // 计算总金额
  const calculateTotalAmount = () => {
    const selectedGift = giftList.find(g => g.giftId === selectedGiftId);
    if (!selectedGift) return 0;
    return selectedGift.giftPrice * giftQuantity * botCount;
  };

  // 判断是否可以打赏
  const canSendGift = previewResult && !previewResult.needRecharge && !loading;

  // 获取步骤状态Tag
  const getStepStatusTag = (status: string) => {
    const config: Record<string, { color: string; icon: React.ReactNode; text: string }> = {
      pending: { color: 'default', icon: <LoadingOutlined />, text: '等待' },
      running: { color: 'processing', icon: <SyncOutlined spin />, text: '执行中' },
      success: { color: 'success', icon: <CheckCircleOutlined />, text: '成功' },
      failed: { color: 'error', icon: <CloseCircleOutlined />, text: '失败' },
      skipped: { color: 'default', icon: <ExclamationCircleOutlined />, text: '跳过' },
    };
    const c = config[status] || config.pending;
    return <Tag color={c.color} icon={c.icon}>{c.text}</Tag>;
  };

  // 获取任务状态Tag
  const getTaskStatusTag = (status: string) => {
    const config: Record<string, { color: string; text: string }> = {
      pending: { color: 'default', text: '等待中' },
      running: { color: 'processing', text: '执行中' },
      completed: { color: 'success', text: '已完成' },
      failed: { color: 'error', text: '失败' },
      cancelled: { color: 'default', text: '已取消' },
    };
    const c = config[status] || config.pending;
    return <Tag color={c.color} style={{ fontWeight: 600 }}>{c.text}</Tag>;
  };

  // 机器人预览表格列
  const botPreviewColumns = [
    { title: '机器人ID', dataIndex: 'botId', key: 'botId', width: 100 },
    { title: '用户名', dataIndex: 'userName', key: 'userName', render: (v: string) => v || '-' },
    {
      title: '钱包地址',
      dataIndex: 'walletAddress',
      key: 'walletAddress',
      render: (addr: string) => (
        <Tooltip title={addr}>
          <Text style={{ color: COLORS.primary, fontFamily: 'monospace' }}>
            {addr.substring(0, 6)}...{addr.substring(addr.length - 4)}
          </Text>
        </Tooltip>
      ),
    },
    {
      title: `${deductCoin}余额`,
      dataIndex: 'balance',
      key: 'balance',
      render: (v: number) => <Text>{v.toFixed(2)}</Text>,
    },
    {
      title: '所需金额',
      dataIndex: 'required',
      key: 'required',
      render: (v: number) => <Text style={{ color: COLORS.warning }}>{v.toFixed(2)}</Text>,
    },
    {
      title: '状态',
      dataIndex: 'sufficient',
      key: 'sufficient',
      render: (sufficient: boolean, record: BotPreview) =>
        sufficient ? (
          <Tag color="success" icon={<CheckCircleOutlined />}>余额充足</Tag>
        ) : (
          <Tag color="warning" icon={<SwapOutlined />}>需转入 {record.needTransfer?.toFixed(2)}</Tag>
        ),
    },
  ];

  // 步骤表格列
  const stepColumns = [
    { title: '序号', dataIndex: 'seq', key: 'seq', width: 60, render: (v: number) => v + 1 },
    {
      title: '类型',
      dataIndex: 'stepType',
      key: 'stepType',
      render: (type: string) => {
        const types: Record<string, string> = { check_balance: '余额检查', transfer: '代币转账', send_gift: '礼物打赏' };
        return types[type] || type;
      },
    },
    {
      title: '机器人',
      dataIndex: 'botName',
      key: 'botName',
      render: (name: string, record: TaskStep) => (
        <Space direction="vertical" size={0}>
          <Text>{name || '-'}</Text>
          {record.walletAddress && <Text style={{ color: 'rgba(0, 0, 0, 0.45)', fontSize: 12, fontFamily: 'monospace' }}>{record.walletAddress.substring(0, 6)}...</Text>}
        </Space>
      ),
    },
    { title: '金额', dataIndex: 'amount', key: 'amount', render: (v: number) => v?.toFixed(2) || '-' },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (status: string, record: TaskStep) => (
        <Space>
          {getStepStatusTag(status)}
          {record.errorMsg && <Tooltip title={record.errorMsg}><CloseCircleOutlined style={{ color: COLORS.error }} /></Tooltip>}
        </Space>
      ),
    },
    {
      title: '交易哈希',
      dataIndex: 'txHash',
      key: 'txHash',
      render: (hash: string) => hash ? <a href={`https://arbiscan.io/tx/${hash}`} target="_blank" rel="noopener noreferrer" style={{ color: COLORS.primary }}>{hash.substring(0, 10)}...</a> : '-',
    },
  ];

  return (
    <div style={{ padding: '24px 32px', minHeight: '100vh', background: COLORS.darkBg }}>
      <PageHeader
        icon={<GiftOutlined />}
        title="礼物打赏"
        subtitle="批量机器人礼物打赏系统"
        color={COLORS.error}
      />

      <Row gutter={24}>
        {/* 左侧表单 */}
        <Col xs={24} lg={10}>
          <SimpleCard title="打赏参数" icon={<GiftOutlined />} color={COLORS.error}>
            <Space direction="vertical" style={{ width: '100%' }} size="large">
              {/* 直播间ID */}
              <div>
                <Text style={{ color: 'rgba(0, 0, 0, 0.45)', marginBottom: 8, display: 'block' }}>直播间ID *</Text>
                <Space.Compact style={{ width: '100%' }}>
                  <Input
                    value={roomId}
                    onChange={(e) => setRoomId(e.target.value)}
                    placeholder="请输入直播间ID"
                  />
                  <Button
                    type="primary"
                    icon={<SearchOutlined />}
                    onClick={fetchGiftList}
                    loading={loadingGifts}
                    disabled={!roomId}
                  >
                    获取礼物
                  </Button>
                </Space.Compact>
              </div>

              {giftList.length > 0 && (
                <>
                  {/* 礼物选择 */}
                  <div>
                    <Text style={{ color: 'rgba(0, 0, 0, 0.45)', marginBottom: 8, display: 'block' }}>选择礼物 *</Text>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                      {giftList.map((gift) => (
                        <div
                          key={gift.giftId}
                          onClick={() => setSelectedGiftId(gift.giftId)}
                          style={{
                            padding: '12px 16px',
                            background: selectedGiftId === gift.giftId ? `${COLORS.error}10` : COLORS.darkBg,
                            border: `2px solid ${selectedGiftId === gift.giftId ? COLORS.error : COLORS.borderDark}`,
                            borderRadius: 8,
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 10,
                            transition: 'all 0.3s',
                          }}
                        >
                          <img src={gift.giftIcon} alt={gift.giftName} style={{ width: 40, height: 40 }} />
                          <div>
                            <div style={{ fontWeight: 500 }}>{gift.giftName}</div>
                            <div style={{ color: COLORS.warning, fontSize: 12 }}>{gift.giftPrice} {deductCoin}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* 数量设置 */}
                  <Row gutter={16}>
                    <Col span={12}>
                      <Text style={{ color: 'rgba(0, 0, 0, 0.45)', marginBottom: 8, display: 'block' }}>礼物数量 *</Text>
                      <InputNumber value={giftQuantity} onChange={(v) => setGiftQuantity(v || 1)} min={1} style={{ width: '100%' }} />
                    </Col>
                    <Col span={12}>
                      <Text style={{ color: 'rgba(0, 0, 0, 0.45)', marginBottom: 8, display: 'block' }}>机器人数量 *</Text>
                      <InputNumber value={botCount} onChange={(v) => setBotCount(v || 1)} min={1} style={{ width: '100%' }} />
                    </Col>
                  </Row>

                  {/* 链选择 */}
                  <div>
                    <Text style={{ color: 'rgba(0, 0, 0, 0.45)', marginBottom: 8, display: 'block' }}>区块链 *</Text>
                    <Select value={chainId} onChange={setChainId} options={CHAIN_OPTIONS} style={{ width: '100%' }} />
                  </div>

                  {/* 总金额 */}
                  <div style={{ padding: 16, background: `${COLORS.warning}10`, borderRadius: 8, border: `1px solid ${COLORS.warning}40` }}>
                    <Row justify="space-between" align="middle">
                      <Text style={{ color: 'rgba(0, 0, 0, 0.45)' }}>总计金额</Text>
                      <Text style={{ color: COLORS.warning, fontSize: 24, fontWeight: 700 }}>
                        {calculateTotalAmount().toFixed(2)} {deductCoin}
                      </Text>
                    </Row>
                  </div>

                  {/* 操作按钮 */}
                  <Space style={{ width: '100%' }} size="middle">
                    <Button icon={<EyeOutlined />} onClick={() => handlePreviewBots(false)} loading={loadingPreview} disabled={!selectedGiftId} style={{ flex: 1, height: 44 }}>
                      预览机器人
                    </Button>
                    <Button type="primary" icon={<SendOutlined />} onClick={handleSendGift} disabled={!canSendGift} loading={loading} style={{ flex: 1, height: 44, background: COLORS.success, borderColor: COLORS.success }}>
                      确认打赏
                    </Button>
                  </Space>
                </>
              )}
            </Space>
          </SimpleCard>
        </Col>

        {/* 右侧结果 */}
        <Col xs={24} lg={14}>
          <Space direction="vertical" style={{ width: '100%' }} size="large">
            {/* 错误提示 */}
            {error && <Alert type="error" message={error} showIcon />}

            {/* 预览结果 */}
            {previewResult && !result && (
              <SimpleCard title="机器人选择预览" icon={<EyeOutlined />} color={COLORS.primary}>
                {/* 余额摘要 */}
                <div style={{ padding: 20, background: previewResult.needRecharge ? `${COLORS.error}08` : `${COLORS.success}08`, borderRadius: 8, border: `1px solid ${previewResult.needRecharge ? COLORS.error : COLORS.success}30`, marginBottom: 20 }}>
                  <Row gutter={[16, 16]}>
                    <Col span={6}>
                      <Statistic title={<Text style={{ color: 'rgba(0, 0, 0, 0.45)' }}>总余额</Text>} value={previewResult.totalBalance} precision={2} suffix={deductCoin} valueStyle={{ color: COLORS.primary, fontSize: 18 }} />
                    </Col>
                    <Col span={6}>
                      <Statistic title={<Text style={{ color: 'rgba(0, 0, 0, 0.45)' }}>所需金额</Text>} value={previewResult.requiredAmount} precision={2} suffix={deductCoin} valueStyle={{ color: COLORS.warning, fontSize: 18 }} />
                    </Col>
                    <Col span={6}>
                      <Statistic title={<Text style={{ color: 'rgba(0, 0, 0, 0.45)' }}>每机器人需要</Text>} value={previewResult.requiredPerBot} precision={2} suffix={deductCoin} valueStyle={{ fontSize: 18 }} />
                    </Col>
                    <Col span={6}>
                      <Statistic title={<Text style={{ color: 'rgba(0, 0, 0, 0.45)' }}>机器人数量</Text>} value={previewResult.selectedCount} suffix={`/ ${previewResult.requestedCount}`} valueStyle={{ fontSize: 18 }} />
                    </Col>
                  </Row>

                  {previewResult.needRecharge && (
                    <div style={{ marginTop: 16 }}>
                      <Text style={{ color: COLORS.error, fontWeight: 600 }}>缺口金额: {previewResult.shortfall?.toFixed(2)} {deductCoin}</Text>
                    </div>
                  )}

                  {previewResult.needTransfer && !previewResult.needRecharge && (
                    <Alert type="warning" message="部分机器人余额不足，将自动从其他机器人转账" icon={<SwapOutlined />} showIcon style={{ marginTop: 16 }} />
                  )}
                </div>

                {/* 需要充值时显示充值信息 */}
                {previewResult.needRecharge && (
                  <div style={{ padding: 20, background: `${COLORS.error}08`, borderRadius: 8, border: `1px solid ${COLORS.error}40`, marginBottom: 20 }}>
                    <Space direction="vertical" style={{ width: '100%' }} size="middle">
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <ExclamationCircleOutlined style={{ color: COLORS.error, fontSize: 20 }} />
                        <Text style={{ color: COLORS.error, fontWeight: 600, fontSize: 16 }}>
                          余额不足，需要充值 {previewResult.shortfall?.toFixed(2)} {deductCoin} 后才能打赏
                        </Text>
                      </div>

                      <Row gutter={24} align="middle">
                        <Col flex="auto">
                          <Space direction="vertical" size="small">
                            <div><Text style={{ color: 'rgba(0, 0, 0, 0.45)' }}>区块链: </Text><Tag color="purple">{getChainName(chainId)} (ID: {chainId})</Tag></div>
                            <div><Text style={{ color: 'rgba(0, 0, 0, 0.45)' }}>代币类型: </Text><Text>{previewResult.rechargeCoin || deductCoin}</Text></div>
                            <div>
                              <Text style={{ color: 'rgba(0, 0, 0, 0.45)' }}>充值地址: </Text>
                              <Space>
                                <Text style={{ color: COLORS.primary, fontFamily: 'monospace', wordBreak: 'break-all' }}>{previewResult.rechargeAddress}</Text>
                                <Button size="small" icon={<CopyOutlined />} onClick={() => { navigator.clipboard.writeText(previewResult.rechargeAddress || ''); message.success('地址已复制'); }} />
                              </Space>
                            </div>
                          </Space>
                        </Col>
                        <Col>
                          <QRCode value={previewResult.rechargeAddress || ''} size={120} />
                        </Col>
                      </Row>

                      {isPolling && (
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 16px', background: COLORS.darkBg, borderRadius: 8 }}>
                          <Space><SyncOutlined spin style={{ color: COLORS.primary }} /><Text>正在检测余额变化...</Text></Space>
                          <Text style={{ color: COLORS.warning }}>{pollingCountdown}秒后刷新</Text>
                        </div>
                      )}
                    </Space>
                  </div>
                )}

                {/* 机器人列表 */}
                {previewResult.selectedBots && previewResult.selectedBots.length > 0 && (
                  <Table columns={botPreviewColumns} dataSource={previewResult.selectedBots} rowKey="botId" pagination={false} size="small" rowClassName={(record) => record.sufficient ? '' : 'insufficient-row'} />
                )}

                {/* 可以打赏提示 */}
                {!previewResult.needRecharge && (
                  <div style={{ marginTop: 16, padding: 12, background: `${COLORS.success}10`, borderRadius: 8, border: `1px solid ${COLORS.success}40`, display: 'flex', alignItems: 'center', gap: 10 }}>
                    <CheckCircleOutlined style={{ color: COLORS.success, fontSize: 18 }} />
                    <Text style={{ color: COLORS.success }}>余额充足，可以进行打赏</Text>
                  </div>
                )}
              </SimpleCard>
            )}

            {/* 任务进度显示 */}
            {taskStatus && (
              <SimpleCard title="任务执行状态" icon={<SyncOutlined spin={isTaskPolling} />} color={COLORS.warning}>
                {/* 任务头信息 */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                  <Text style={{ color: 'rgba(0, 0, 0, 0.45)' }}>任务ID: <Text copyable style={{ color: COLORS.primary }}>{taskStatus.taskId}</Text></Text>
                  {getTaskStatusTag(taskStatus.status)}
                </div>

                {/* 进度条 */}
                <Progress percent={taskStatus.progress} strokeColor={COLORS.primary} format={(percent) => <Text>{taskStatus.completedSteps} / {taskStatus.totalSteps} ({percent}%)</Text>} />

                {/* 统计信息 */}
                <Row gutter={16} style={{ marginTop: 20 }}>
                  <Col span={6}><Statistic title={<Text style={{ color: 'rgba(0, 0, 0, 0.45)' }}>礼物</Text>} value={taskStatus.giftName || `ID: ${taskStatus.giftId}`} valueStyle={{ fontSize: 14 }} /></Col>
                  <Col span={6}><Statistic title={<Text style={{ color: 'rgba(0, 0, 0, 0.45)' }}>总花费</Text>} value={taskStatus.totalCost?.toFixed(2)} suffix={taskStatus.deductCoin} valueStyle={{ color: COLORS.warning, fontSize: 14 }} /></Col>
                  <Col span={6}><Statistic title={<Text style={{ color: 'rgba(0, 0, 0, 0.45)' }}>成功</Text>} value={taskStatus.successCount} valueStyle={{ color: COLORS.success, fontSize: 14 }} /></Col>
                  <Col span={6}><Statistic title={<Text style={{ color: 'rgba(0, 0, 0, 0.45)' }}>失败</Text>} value={taskStatus.failedCount} valueStyle={{ color: COLORS.error, fontSize: 14 }} /></Col>
                </Row>

                {/* 错误信息 */}
                {taskStatus.errorMsg && <Alert type="error" message={taskStatus.errorMsg} style={{ marginTop: 16 }} />}

                {/* 步骤详情 */}
                {taskStatus.steps && taskStatus.steps.length > 0 && (
                  <div style={{ marginTop: 20 }}>
                    <Text style={{ fontWeight: 600, marginBottom: 12, display: 'block' }}>执行步骤</Text>
                    <Table columns={stepColumns} dataSource={taskStatus.steps} rowKey="seq" pagination={false} size="small" scroll={{ y: 300 }} />
                  </div>
                )}

                {/* 轮询状态 */}
                {isTaskPolling && (
                  <div style={{ marginTop: 16, display: 'flex', alignItems: 'center', gap: 8, color: COLORS.primary }}>
                    <SyncOutlined spin />
                    <Text style={{ color: COLORS.primary }}>正在监听任务状态...</Text>
                  </div>
                )}
              </SimpleCard>
            )}

            {/* 结果展示 */}
            {result && (
              <SimpleCard
                title={result.needRecharge ? '余额不足' : result.success ? '打赏完成' : '打赏失败'}
                icon={result.success ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
                color={result.success ? COLORS.success : COLORS.error}
              >
                {result.needRecharge ? (
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <Row gutter={16}>
                      <Col span={8}><Statistic title="当前总余额" value={result.totalBalance?.toFixed(2)} suffix={result.rechargeCoin} valueStyle={{ color: COLORS.primary }} /></Col>
                      <Col span={8}><Statistic title="所需金额" value={result.requiredAmount?.toFixed(2)} suffix={result.rechargeCoin} valueStyle={{ color: COLORS.warning }} /></Col>
                      <Col span={8}><Statistic title="缺口金额" value={result.shortfall?.toFixed(2)} suffix={result.rechargeCoin} valueStyle={{ color: COLORS.error }} /></Col>
                    </Row>
                    <div style={{ marginTop: 20, textAlign: 'center' }}>
                      <Text style={{ color: 'rgba(0, 0, 0, 0.45)' }}>请充值到以下地址:</Text>
                      <div style={{ marginTop: 8 }}><Tag color="purple">{getChainName(chainId)}</Tag></div>
                      <Text copyable style={{ color: COLORS.primary, marginTop: 8, display: 'block' }}>{result.rechargeAddress}</Text>
                      <div style={{ marginTop: 16 }}><QRCode value={result.rechargeAddress || ''} size={150} /></div>
                    </div>
                  </Space>
                ) : result.success ? (
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <Row gutter={16}>
                      <Col span={8}><Statistic title="总计" value={result.total} suffix="个机器人" /></Col>
                      <Col span={8}><Statistic title="成功" value={result.successCount} valueStyle={{ color: COLORS.success }} /></Col>
                      <Col span={8}><Statistic title="失败" value={result.failCount} valueStyle={{ color: COLORS.error }} /></Col>
                    </Row>
                    {result.results && result.results.length > 0 && (
                      <Table
                        columns={[
                          { title: '机器人ID', dataIndex: 'botId', key: 'botId' },
                          { title: '钱包地址', dataIndex: 'walletAddress', key: 'walletAddress', render: (addr: string) => <Tooltip title={addr}><Text style={{ color: COLORS.primary, fontFamily: 'monospace' }}>{addr.substring(0, 6)}...{addr.substring(addr.length - 4)}</Text></Tooltip> },
                          { title: '余额', dataIndex: 'balance', key: 'balance', render: (v: number) => v.toFixed(2) },
                          { title: '状态', dataIndex: 'success', key: 'success', render: (success: boolean) => success ? <Tag color="success" icon={<CheckCircleOutlined />}>成功</Tag> : <Tag color="error" icon={<CloseCircleOutlined />}>失败</Tag> },
                          { title: '订单号', dataIndex: 'orderNo', key: 'orderNo', render: (v: string) => v || '-' },
                          { title: '交易哈希', dataIndex: 'transactionHash', key: 'transactionHash', render: (hash: string) => hash ? <a href={`https://bscscan.com/tx/${hash}`} target="_blank" rel="noopener noreferrer" style={{ color: COLORS.primary }}>{hash.substring(0, 10)}...</a> : '-' },
                        ]}
                        dataSource={result.results}
                        rowKey="botId"
                        pagination={false}
                        size="small"
                      />
                    )}
                  </Space>
                ) : (
                  <Alert type="error" message={result.message || result.error} />
                )}
              </SimpleCard>
            )}
          </Space>
        </Col>
      </Row>
    </div>
  );
};

export default GiftSend;
